import { useRef, useState, useEffect } from "react";

// Max 50
export const MAX_CACHE_SIZE = () => 50;

/**
 * Stable stringify object
 * @param {object} obj
 * @returns {string} stringified object
 */
const stableStringify = (obj) => {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const sortedKeys = Object.keys(obj).sort();
    const sortedObj = {};
    for (const key of sortedKeys) {
      sortedObj[key] = stableStringify(obj[key]);
    }
    return JSON.stringify(sortedObj);
  }
  if (Array.isArray(obj)) {
    return JSON.stringify(obj.map(stableStringify));
  }
  return obj;
};

/**
 * Async SHA-256 hash function for string input
 * @param {string} str
 * @returns {string}
 */
const sha256 = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

export const useDebounce = (value, delay, bypass = false) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (bypass) {
      setDebounced(value);
      return;
    }

    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay, bypass]);

  return debounced;
};

const useDebouncedHashedParams = (params, debounceDelay = 300) => {
  const cacheRef = useRef(new Map());
  const [cacheKey, setCacheKey] = useState(null);

  // Generate hash key on param change
  useEffect(() => {
    let isActive = true;

    (async () => {
      const stableStr = stableStringify(params);
      const key = await sha256(stableStr);
      if (!isActive) return;
      setCacheKey(key);
    })();

    return () => {
      isActive = false;
    };
  }, [params]);

  const debouncedParams = useDebounce(params, debounceDelay, hasCache);

  const setCache = async (rawParams, result) => {
    if (cacheRef.current.size >= MAX_CACHE_SIZE) {
      const firstKey = cacheRef.current.keys().next().value;
      cacheRef.current.delete(firstKey);
    }

    const stableStr = stableStringify(rawParams);
    const key = await sha256(stableStr);
    cacheRef.current.set(key, JSON.stringify(result));
  };

  const getCache = async (rawParams) => {
    const stableStr = stableStringify(rawParams);
    const key = await sha256(stableStr);
    const stringified = cacheRef.current.get(key);
    return JSON.parse(stringified);
  };

  const hasCache = () => {
    return cacheRef.current.has(cacheKey) ?? false;
  };

  return {
    debouncedParams,
    hasCache,
    setCache,
    getCache,
  };
};

export default useDebouncedHashedParams;
